/**
 * Textos de exibição do card "Reuniões" da Home (card 3786). Funções puras: as datas chegam já
 * quebradas em partes pelo `DateFormatterService` de quem chama (regra de datas do projeto).
 */

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

/** Dia da semana ISO (1 = segunda ... 7 = domingo). */
const WEEKDAY_ABBREVIATIONS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

/** `10` → `"Out"`. Fora de 1..12, `""`. */
export function monthAbbreviation(month: number): string {
  return MONTH_ABBREVIATIONS[month - 1] ?? '';
}

/** `4` (ISO) → `"Qui"`. Fora de 1..7, `""`. */
export function weekdayAbbreviation(isoWeekday: number): string {
  return WEEKDAY_ABBREVIATIONS[isoWeekday - 1] ?? '';
}

/** `42` → `"42 min"`, `60` → `"1 h"`, `90` → `"1 h 30 min"`. Sem duração, `null`. */
export function formatDuration(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }

  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;

  if (hours === 0) {
    return `${rest} min`;
  }

  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** `3` → `"3 participantes"`, `1` → `"1 participante"`. Zero ou vazio, `null`. */
export function formatParticipants(count: number | null | undefined): string | null {
  if (!count || count <= 0) {
    return null;
  }

  return count === 1 ? '1 participante' : `${count} participantes`;
}

/** Junta as partes que existem com " · ": `["Qui, 10:00", "1 h", "Teams"]` → `"Qui, 10:00 · 1 h · Teams"`. */
export function joinMeetingParts(parts: (string | null | undefined)[]): string {
  return parts.filter((part): part is string => !!part && part.trim() !== '').join(' · ');
}

/** Itens que cabem no card e quantos ficaram de fora (crítica 3819, G2). */
export interface TruncatedList<T> {
  visible: T[];
  hiddenCount: number;
}

/** Os primeiros `max` itens e a contagem do resto: `(["a","b","c","d"], 3)` → `{ visible: ["a","b","c"], hiddenCount: 1 }`. */
export function truncateList<T>(
  items: readonly T[] | null | undefined,
  max: number
): TruncatedList<T> {
  const list = items ?? [];
  const limit = Math.max(0, Math.floor(max));
  return { visible: list.slice(0, limit), hiddenCount: Math.max(0, list.length - limit) };
}

/** `(2, "decisão", "decisões")` → `"+ 2 decisões"`, `(1, ...)` → `"+ 1 decisão"`. Nada escondido, `null`. */
export function hiddenItemsLabel(
  hiddenCount: number,
  singular: string,
  plural: string
): string | null {
  if (!hiddenCount || hiddenCount <= 0) {
    return null;
  }

  return `+ ${hiddenCount} ${hiddenCount === 1 ? singular : plural}`;
}

/** Dias corridos desde 01/03/0000 (algoritmo civil), só aritmética: evita `Date` nativo no util. */
function civilDayNumber(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const monthIndex = month > 2 ? month - 3 : month + 9;
  const dayOfYear = Math.floor((153 * monthIndex + 2) / 5) + day - 1;
  const dayOfEra =
    yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
  return era * 146097 + dayOfEra;
}

function parseDateKey(key: string | null | undefined): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key ?? '');
  if (!match) {
    return null;
  }

  const [, year, month, day] = match.map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  return civilDayNumber(year, month, day);
}

/**
 * Dias de calendário entre duas datas `yyyy-MM-dd` (já no fuso da pessoa, pelo `DateFormatterService`):
 * `("2026-10-30", "2026-11-04")` → `5`. Data inválida, `null`.
 */
export function calendarDaysBetween(fromKey: string, toKey: string): number | null {
  const from = parseDateKey(fromKey);
  const to = parseDateKey(toKey);
  return from === null || to === null ? null : to - from;
}

/** Distância da próxima reunião (crítica 3819, A5): `0` → `"Hoje"`, `1` → `"Amanhã"`, `5` → `"Em 5 dias"`. Passado ou vazio, `null`. */
export function relativeDayLabel(days: number | null | undefined): string | null {
  if (days === null || days === undefined || !Number.isFinite(days) || days < 0) {
    return null;
  }

  if (days === 0) {
    return 'Hoje';
  }

  return days === 1 ? 'Amanhã' : `Em ${days} dias`;
}
